import { Form, Input, Button, Modal, Space, Divider, Row as AntRow, Col } from "antd";
import { useState, useEffect } from "react";
import { useDispatchRoot } from "../../redux/store";
import { editAuthorRequest } from "../../redux/controller";
import { IAuthor } from "../../common/author.interface";
import { 
    UserOutlined, 
    FileTextOutlined, 
    EditOutlined, 
    CloseOutlined 
} from "@ant-design/icons";
import { confirmCloseModal } from "../modalEditBook/CModalConfirmClose";
import './CModal.styles.scss'
import TextArea from "antd/lib/input/TextArea";

interface MyProps {
    open: boolean;
    data?: IAuthor;
    setOpenModalEdit: React.Dispatch<React.SetStateAction<boolean>>,
}




const CModalEditAuthor = (props: MyProps) => {

    const dispatch = useDispatchRoot();
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const values = Form.useWatch([], form);



    useEffect(() => {
        if (props.data) {
            form.setFieldsValue({
                name: props.data.name || getAuthorFullName(props.data),
                description: props.data.description || '',
                birthYear: props.data.birthYear?.toString() || '',
            });
        }
    }, [props.data, form]);

    // Helper function để tạo tên đầy đủ từ firstName, lastName, middleName (tương thích ngược)
    const getAuthorFullName = (author: IAuthor) => {
        if (author.name) return author.name;
        
        let fullName = '';
        if (author.firstName) fullName += author.firstName;
        if (author.middleName) fullName += (fullName ? ' ' : '') + author.middleName;
        if (author.lastName) fullName += (fullName ? ' ' : '') + author.lastName;
        return fullName;
    };

    useEffect(() => {
        const currentValues = form.getFieldsValue();
        const originalName = props.data?.name || getAuthorFullName(props.data || {} as IAuthor);
        const originalDescription = props.data?.description || '';
        const originalBirthYear = props.data?.birthYear?.toString() || '';
        
        const hasFormChanges = 
            currentValues.name !== originalName ||
            currentValues.description !== originalDescription ||
            currentValues.birthYear !== originalBirthYear;
        
        setHasChanges(hasFormChanges);
    }, [values, props.data, form]);

    const handleEditAuthor = async (value: any) => {
        setIsLoading(true);
        try {
            const bodyRequest = {
                id: props.data?.id,
                name: value.name,
                description: value.description,
                birthYear: value.birthYear ? parseInt(value.birthYear) : undefined,
            }

            await dispatch(editAuthorRequest(bodyRequest));
            props.setOpenModalEdit(false);
        } catch (error) {
            console.error('Error updating author:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEditAuthor = () => {
        confirmCloseModal({
            hasChanges,
            onConfirm: () => {
                resetForm();
                props.setOpenModalEdit(false);
            },
            title: 'Xác nhận đóng',
            content: 'Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn đóng modal chỉnh sửa tác giả?'
        });
    };

    const resetForm = () => {
        setHasChanges(false);
        form.resetFields();
    };

    return (
        <Modal
            open={props.open}
            onCancel={handleCancelEditAuthor}
            title={
                <Space>
                    <EditOutlined />
                    <span>Chỉnh sửa tác giả</span>
                </Space>
            }
            className="author-edit-modal"
            footer={null}
            width={600}
            destroyOnClose
            maskClosable={true}
        >
            <div className="modal-content">
                <Form
                    initialValues={props.data}
                    form={form}
                    className="author-form"
                    layout="vertical"
                    onFinish={handleEditAuthor}
                >
                    <AntRow gutter={[16, 12]}>
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <Space>
                                        <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                                        <span>Tên đầy đủ tác giả</span>
                                    </Space>
                                }
                                name="name"
                                rules={[{ required: true, message: 'Tên tác giả không được để trống!' }]}
                            >
                                <Input
                                    placeholder="Nhập tên đầy đủ tác giả (ví dụ: Nguyễn Nhật Ánh)"
                                    className="form-input"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={
                                    <Space>
                                        <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                                        <span>Năm sinh</span>
                                    </Space>
                                }
                                name="birthYear"
                                rules={[
                                    { 
                                        pattern: /^\d{4}$/, 
                                        message: "Năm sinh phải là 4 chữ số!" 
                                    },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            const year = parseInt(value);
                                            if (year < 1900 || year > new Date().getFullYear()) {
                                                return Promise.reject(new Error('Năm sinh phải từ 1900 đến hiện tại!'));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="Nhập năm sinh (ví dụ: 1955)"
                                    className="form-input"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <div style={{ height: '36px' }}></div>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={
                                    <Space>
                                        <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                                        <span>Mô tả về tác giả</span>
                                    </Space>
                                }
                                name="description"
                            >
                                <TextArea
                                    placeholder="Nhập mô tả về tác giả (tùy chọn)"
                                    className="form-textarea"
                                    rows={4}
                                    maxLength={1000}
                                    showCount
                                />
                            </Form.Item>
                        </Col>
                    </AntRow>

                    <Divider style={{ margin: '16px 0' }} />

                    <div className="modal-actions">
                        <Button
                            onClick={handleCancelEditAuthor}
                            className="cancel-button"
                            icon={<CloseOutlined />}
                        >
                            Hủy
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="save-button"
                            icon={<EditOutlined />}
                            loading={isLoading}
                        >
                            Cập nhật
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default CModalEditAuthor;
